import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowCounterClockwise, MagnifyingGlass, Warning, X, Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

export function POSReturns({ prefillSale, onClose }) {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [searchFolio, setSearchFolio] = useState("");
  const [sale, setSale] = useState(prefillSale || null);
  const [selectedItems, setSelectedItems] = useState({});
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [prevReturns, setPrevReturns] = useState([]);

  const searchSale = async () => {
    if (!searchFolio.trim()) return;
    try {
      const res = await axios.get(`${API}/pos/sales?limit=200`, { headers });
      const found = res.data.find(s => s.folio?.toLowerCase() === searchFolio.trim().toLowerCase());
      if (found) {
        setSale(found);
        setSelectedItems({});
        // Fetch previous returns for this sale
        const retRes = await axios.get(`${API}/pos/returns`, { headers });
        const saleReturns = retRes.data.filter(r => r.sale_id === found.id);
        setPrevReturns(saleReturns);
      }
      else toast.error("Venta no encontrada");
    } catch { toast.error("Error al buscar"); }
  };

  // Calculate already returned quantities per item
  const alreadyReturned = {};
  prevReturns.forEach(pr => {
    (pr.items || []).forEach(pi => {
      const key = pi.sku || pi.product_id || pi.name;
      alreadyReturned[key] = (alreadyReturned[key] || 0) + (pi.return_qty || 0);
    });
  });

  const toggleItem = (idx, checked) => {
    setSelectedItems(prev => {
      const next = { ...prev };
      if (checked) {
        const item = sale.items[idx];
        const key = item.sku || item.product_id || item.name;
        const prevQty = alreadyReturned[key] || 0;
        const maxReturn = Math.max(item.quantity - prevQty, 0);
        if (maxReturn <= 0) { toast.error(`"${item.name}" ya fue devuelto completamente`); return prev; }
        next[idx] = { ...item, return_qty: maxReturn, max_returnable: maxReturn };
      } else {
        delete next[idx];
      }
      return next;
    });
  };

  const totalReturn = Object.values(selectedItems).reduce((sum, i) => sum + (i.return_qty * i.unit_price), 0);

  const handleSubmit = async () => {
    const items = Object.values(selectedItems).filter(i => i.return_qty > 0);
    if (items.length === 0) { toast.error("Selecciona items a devolver"); return; }
    if (!reason) { toast.error("Selecciona un motivo"); return; }

    setProcessing(true);
    try {
      const res = await axios.post(`${API}/pos/returns`, {
        sale_id: sale.id,
        items: items.map(i => ({
          product_id: i.product_id,
          sku: i.sku,
          name: i.name,
          item_type: i.item_type,
          original_qty: i.quantity,
          return_qty: i.return_qty,
          unit_price: i.unit_price,
        })),
        reason,
        notes,
      }, { headers });
      toast.success(`Devolucion ${res.data.folio} procesada. Total: $${res.data.total_return?.toLocaleString("es-MX")}`);
      setSale(null);
      setSelectedItems({});
      setReason("");
      setNotes("");
      onClose?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al procesar"); }
    setProcessing(false);
  };

  return (
    <div className="space-y-5 max-w-5xl" data-testid="pos-returns">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ArrowCounterClockwise size={24} weight="duotone" className="text-red-600" /> Devolucion de Mercancia</h1>
          <p className="text-sm text-slate-500">Busca la venta original y selecciona los items a devolver</p>
        </div>
        {onClose && <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>}
      </div>

      {/* Search */}
      {!sale && (
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={searchFolio} onChange={e => setSearchFolio(e.target.value)} onKeyDown={e => e.key === "Enter" && searchSale()}
              placeholder="Buscar folio de venta (ej: NV-00001)..." className="h-10 pl-8 text-sm font-mono" data-testid="pos-return-search" />
          </div>
          <Button className="h-10" onClick={searchSale}>Buscar</Button>
        </div>
      )}

      {sale && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
          {/* Left: Sale + Items */}
          <div className="space-y-4">
            {/* Sale info */}
            <div className="bg-white border rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-slate-800">Venta Original: <span className="text-blue-700 font-mono">{sale.folio}</span></p>
                <p className="text-xs text-slate-500">{sale.customer_name} · {sale.created_at?.slice(0, 10)} · Total: ${sale.total?.toLocaleString("es-MX")}</p>
              </div>
              <button onClick={() => { setSale(null); setSelectedItems({}); }} className="text-xs text-blue-600 hover:underline">Cambiar venta</button>
            </div>

            {/* Items to return */}
            <div className="bg-white border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b text-left">
                    <th className="px-3 py-2.5 w-10"></th>
                    <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">SKU</th>
                    <th className="px-3 py-2.5 text-xs font-semibold text-slate-600">Producto</th>
                    <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Cant Original</th>
                    <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-center">Cant a Devolver</th>
                    <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-right">P. Unit</th>
                    <th className="px-3 py-2.5 text-xs font-semibold text-slate-600 text-right">Importe Dev.</th>
                  </tr>
                </thead>
                <tbody>
                  {(sale.items || []).map((item, idx) => {
                    const sel = selectedItems[idx];
                    const key = item.sku || item.product_id || item.name;
                    const prevQty = alreadyReturned[key] || 0;
                    const maxReturn = Math.max(item.quantity - prevQty, 0);
                    const fullyReturned = maxReturn <= 0;
                    return (
                      <tr key={idx} className={`border-b ${sel ? "bg-red-50/50" : ""} ${fullyReturned ? "opacity-40" : ""}`}>
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={!!sel} onChange={e => toggleItem(idx, e.target.checked)} disabled={fullyReturned} className="w-4 h-4 rounded border-slate-300 text-red-600 disabled:opacity-30" />
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-slate-600">{item.sku}</td>
                        <td className="px-3 py-2 text-xs font-semibold text-slate-800">
                          {item.name}
                          {prevQty > 0 && <span className="ml-2 text-[9px] text-amber-600 font-normal">(ya devueltos: {prevQty})</span>}
                        </td>
                        <td className="px-3 py-2 text-center text-xs font-mono">{item.quantity}</td>
                        <td className="px-3 py-2 text-center">
                          {sel ? (
                            <input type="number" min={1} max={maxReturn} value={sel.return_qty}
                              onChange={e => setSelectedItems(prev => ({ ...prev, [idx]: { ...prev[idx], return_qty: Math.min(maxReturn, Math.max(1, parseInt(e.target.value) || 1)) } }))}
                              className="w-14 h-6 text-center text-xs font-mono border rounded" />
                          ) : fullyReturned ? <span className="text-[9px] text-red-500 font-bold">DEVUELTO</span> : <span className="text-xs text-slate-400">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right text-xs font-mono">${item.unit_price?.toLocaleString("es-MX")}</td>
                        <td className="px-3 py-2 text-right text-xs font-mono font-bold text-red-700">{sel ? `$${(sel.return_qty * item.unit_price).toLocaleString("es-MX")}` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: Summary */}
          <div className="space-y-4">
            <div className="bg-white border-2 border-red-200 rounded-xl p-4 space-y-4">
              <p className="text-[10px] font-semibold text-red-600 uppercase">Resumen de Devolucion</p>
              <div className="text-center py-3 bg-red-50 rounded-lg">
                <p className="text-2xl font-extrabold text-red-700 font-mono">${totalReturn.toLocaleString("es-MX")}</p>
                <p className="text-[10px] text-red-500">Total a devolver</p>
              </div>

              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase">Motivo de devolucion</label>
                <select value={reason} onChange={e => setReason(e.target.value)} className="w-full h-9 px-3 border rounded-lg text-sm bg-white mt-1" data-testid="pos-return-reason">
                  <option value="">Seleccionar...</option>
                  <option value="defectuoso">Producto defectuoso</option>
                  <option value="cambio_opinion">Cambio de opinion</option>
                  <option value="error_venta">Error en venta</option>
                  <option value="garantia">Garantia</option>
                  <option value="otro">Otro</option>
                </select>
              </div>

              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase">Notas</label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observaciones..." className="text-sm mt-1 min-h-[60px]" />
              </div>

              <Button className="w-full h-10 bg-red-600 hover:bg-red-700 text-white font-bold" onClick={handleSubmit} disabled={processing || Object.keys(selectedItems).length === 0} data-testid="pos-return-submit">
                <Warning size={16} className="mr-2" /> {processing ? "Procesando..." : "Procesar Devolucion"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
